/* eslint-disable */

import React from 'react';
import Image from 'next/image';

// resources
import mapImg from '../assets/images/contact/map-img.jpg';

import { phpApi } from '../utils/AxiosInstance';

import styles from '../styles/contact.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch } from '@fortawesome/free-solid-svg-icons';

const supportMobileNumber = {
  dash: '076-4000-3000',
  num: '7640003000',
};

const defaultErrors = {
  name: null,
  email: null,
  phone: null,
  subject: null,
  enquiry: null,
};

function Contact() {
  const formRef = React.createRef();
  const [validated, setValidated] = React.useState(false);
  const [errors, setErrors] = React.useState(defaultErrors);

  const [message, setMessage] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(null);
  const [isSuccess, setIsSuccess] = React.useState(null);

  // Mail Sent Message Handling Code
  React.useEffect(() => {
    if (message && !isLoading) {
      if (isSuccess === false) {
        // Reset Form
        formRef?.current?.reset();
        setValidated(false);
        setErrors(defaultErrors);
      }
      // Clear Message after 3 seconds for best UX
      setTimeout(() => {
        setIsSuccess(null);
        setMessage(null);
        formRef?.current?.reset();
      }, [5000]);
    }
  }, [message, isLoading]);

  const handleContactSubmit = (event) => {
    event.preventDefault();

    let data = Object.fromEntries(new FormData(event.currentTarget));
    if (!validateFormData(data)) return;

    const formData = new FormData();
    formData.append('name', `${data.name}`.trim());
    formData.append('email', `${data.email}`.trim());
    formData.append('phone_number', `${data.phone}`);
    formData.append('subject', `${data.subject}`.trim());
    formData.append('enquiry', `${data.enquiry}`.trim());

    setIsLoading(true);

    phpApi.post(`contact_us`, formData)
      .then((response) => {
        if (response.data.success === 1) {
          setIsSuccess(true);
          setMessage(response.data.msg);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  // Email Validation
  const validateEmail = (email) => {
    return email.match(
      /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    );
  };

  const validateFormData = (formData) => {
    let errors = { ...defaultErrors };

    errors.name = validateFieldValue('name', formData.name);
    errors.email = validateFieldValue('email', formData.email);
    errors.phone = validateFieldValue('phone', formData.phone);
    errors.subject = validateFieldValue('subject', formData.subject);
    errors.enquiry = validateFieldValue('enquiry', formData.enquiry);

    setErrors(errors);
    setValidated(true);

    return !Object.values(errors).some((error) => error !== null);
  };

  const validateFieldValue = (name, value) => {
    let error = null;

    // Different Validation Checks
    switch (name.toLowerCase()) {
      case 'name':
        if (value.trim().length === 0 || value.trim().length < 2) {
          error = 'Your Name is Required.';
        } else if (
          value.trim().length &&
          /^([A-Z\.\s]+)$/i.test(value) === false
        ) {
          error = 'Name must contains alphabets and spaces only';
        } else if (value.trim().length && value.trim().length > 150) {
          error = 'Name must contains less than 150 characters';
        }
        break;
      case 'email':
        if (value.trim().length === 0) {
          error = 'E-Mail Address is Required.';
        } else if (!validateEmail(value)) {
          error = 'Invalid E-Mail Address provided.';
        }
        break;
      case 'phone':
        if (value.length === 0) {
          error = 'Phone Number is Required.';
        } else if (value.length != 10) {
          error = 'Invalid Phone Number provided.';
        }
        break;
      case 'subject':
        if (value.trim().length === 0 || value.trim().length < 2) {
          error = 'Subject is Required.';
        } else if (
          value.trim().length &&
          /^([A-Z\.\s]+)$/i.test(value) === false
        ) {
          error = 'Subject must contains alphabets and spaces only';
        } else if (value.trim().length && value.trim().length > 500) {
          error = 'Subject must contains less than 500 characters';
        }
        break;
      case 'enquiry':
        if (value.trim().length === 0 || value.trim().length < 2) {
          error = 'Enquiry is Required.';
        } else if (
          value.trim().length &&
          /^([A-Z\.\s]+)$/i.test(value) === false
        ) {
          error = 'Enquiry must contains alphabets and spaces only';
        } else if (value.trim().length && value.trim().length > 2000) {
          error = 'Enquiry must contains less than 2000 characters';
        }
        break;
    }

    const $fieldElem = formRef.current.querySelector(
      `#contact-form *[name="${name}"]`,
    );

    if (error !== null) {
      try {
        $fieldElem.setCustomValidity(error);
      } catch (e) { }
    } else {
      $fieldElem.setCustomValidity('');
    }

    return error;
  };

  const validateField = (event) => {
    let field = event.currentTarget;

    let error = validateFieldValue(field.name, field.value);

    setErrors({
      ...errors,
      [field.name]: error,
    });
  };

  return (
    <wrapper className={styles.contactPage}>
      <section className={styles.pageContent}>
        <header className={styles.pageHeader}>
          <h2 className={styles.pageTitle}>
            Get in <span>Touch!</span>
          </h2>
          <p className={styles.pageSubtitle}>
            Have any questions or doubts? We are just a call away. You can
            also drop down at our office <br /> and we&apos;ll chat!
          </p>
        </header>

        <div className={styles.actionsRow}>
          <div className={styles.actionCol}>
            <div className={styles.actionContent}>
              <h3 className={styles.actionLabel}>Corporate Office</h3>
              <span>Unit 209, 210, Tower A, 2nd Floor,</span>
              <p>
                Unitech Cyber Park Sector 39,
                <br />
                Gurugram - 122002
              </p>
            </div>
          </div>

          <div className={styles.actionCol}>
            <div className={styles.actionContent}>
              <h3 className={styles.actionLabel}>Call us To Schedule</h3>
              <span>CALL US TODAY!</span>
              <p>
                Call <a href={`tel:${supportMobileNumber?.num}`}>{supportMobileNumber?.num}</a>
              </p>
            </div>
          </div>

          <div className={styles.actionCol}>
            <div className={styles.actionContent}>
              <h3 className={styles.actionLabel}>Send Us An Email</h3>
              <span>WE'RE STANDING BY!</span>
              <p>
                Email:
                <a href="mailto:faculty.counseling@studyiq.com">faculty.counseling@studyiq.com</a> today
              </p>
            </div>
          </div>
        </div>

        <div className={styles.formAndMapContainer}>
          {/* Show Loader once user request to sent query  */}
          {isLoading && (
            <div className={styles.contactUsLoadingContainer}>
              <div className={styles.contactUsLoadingLoader}>
                <FontAwesomeIcon icon={faCircleNotch} size="4x" spin />
              </div>
            </div>
          )}

          <div className={styles.contactFormWrapper}>
            <h3 className={styles.contactFormTitle}>Send us a message</h3>

            <form
              id="contact-form"
              className={styles.contactForm}
              noValidate
              validated={validated}
              onSubmit={handleContactSubmit}
              ref={formRef}
            >
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="contact--your-name">Your Name</label>
                  <input
                    id="contact--your-name"
                    name="name"
                    type="text"
                    onChange={validateField.bind(this)}
                    required
                    autoComplete="off"
                    isInvalid={errors.name}
                  />
                  {errors.name && (
                    <span className={styles.formInputError}>
                      {errors?.name}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="contact--your-email">
                    E-Mail Address
                  </label>
                  <input
                    id="contact--your-email"
                    name="email"
                    type="email"
                    onChange={validateField.bind(this)}
                    required
                    autoComplete="off"
                    isInvalid={errors.email}
                  />
                  {errors.email && (
                    <span className={styles.formInputError}>
                      {errors?.email}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="contact--phone">
                    Phone Number
                  </label>
                  <input
                    id="contact--phone"
                    type="number"
                    name="phone"
                    maxLength="10"
                    onChange={validateField.bind(this)}
                    required
                    autoComplete="off"
                    isInvalid={errors.phone}
                  />
                  {errors.phone && (
                    <span className={styles.formInputError}>
                      {errors?.phone}
                    </span>
                  )}
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="contact--subject">Subject</label>
                  <input
                    id="contact--subject"
                    type="text"
                    name="subject"
                    onChange={validateField.bind(this)}
                    required
                    autoComplete="off"
                  />
                  {errors.subject && (
                    <span className={styles.formInputError}>
                      {errors?.subject}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label htmlFor="contact--enquiry">Enquiry</label>
                  <textarea
                    as="textarea"
                    id="contact--enquiry"
                    name="enquiry"
                    style={{ height: '9.375rem' }}
                    onChange={validateField.bind(this)}
                    required
                    autoComplete="off"
                  />
                  {errors.enquiry && (
                    <span className={styles.formInputError}>
                      {errors?.enquiry}
                    </span>
                  )}
                </div>
              </div>

              <div className={styles.formRow}>
                <div className="message-container pr-3" style={{ flex: 1 }}>
                  {/* Show  message once request sent. */}
                  {message && (
                    <span
                      style={{
                        color: !isSuccess ? 'red' : 'green',
                        fontWeight: 'bold',
                      }}>
                      {message}
                    </span>
                  )}
                </div>
                <div className="ml-auto mr-1">
                  <button type="submit" className={styles.buttonSubmit}>
                    Submit
                  </button>
                </div>
              </div>
            </form>
          </div>
          <div className={styles.contactMap}>
            <h3 className={styles.contentMapTitle}>Location</h3>
            <span style={{ fontSize: '0.875rem' }}>Find us here!</span>

            <div className={styles.mapArea}>
              <Image src={mapImg} height={360} width={530} alt="Map" />
            </div>
          </div>
        </div>
      </section>
    </wrapper>
  );
};

export default Contact;
